import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { getImageInfo, uploadImage, imageProcess } from 'api/image';
import PhotoUploader from 'pages/AccountSetUp/PhotoCropper/ImageUploader';
import PhotoCropper from 'pages/AccountSetUp/PhotoCropper/ImageCropper';

import {
  fetchImageStatus,
  fetchImage,
} from 'apollo/images.query';

import './styles.scss';

const imageSizeLimit = Number(process.env.REACT_APP_IMAGE_SIZE_LIMIT);

const ImageUploadField = ({
  label,
  imageUploadType = 'photo',
  imageUrl,
  originalImageKey,
  updateImageKey,
  updateOriginalImageKey,
  updateImageUrl,
  imageInfoPath,
  imageProcessPath,
  setImageUpdating,
  setImageModalOpen,
  cropperOnly = false,
  openCropper = false,
  imageUploadText,
  imageCropperUploadText,
  cropperConfig,
  errorCallback,
}) => {
  const [originalImg, setOriginalImage] = useState(null);
  const [uploadingImg, setUploadingImage] = useState(null);
  const [imageName, setImageName] = useState('');
  const [openImageCropper, setImageCropperOpen] = useState(false);
  const [isImgInvalid, setImgInvalid] = useState(false);
  const [errorTypes, setErrorTypes] = useState(null);
  const [imgLoadStatus, setImageLoadStatus] = useState('');

  let timer;
  const getImageStatus = async (key) => {
    try {
      const dataStatus = await fetchImageStatus(key);
      if (dataStatus.image.status === 'processed') {
        clearInterval(timer);
        updateImageKey(key);
        setImageLoadStatus('success');
      }
      else if (dataStatus.image.status === 'error') {
        clearInterval(timer);
        setImageLoadStatus('failed');
      }
    } catch (err) {
      clearInterval(timer);
      setImageLoadStatus('failed');
    }
  };

  const getOriginalImage = async (key) => {
    if (key) {
      const imageB64 = true;
      try {
        const data = await fetchImage(key, imageB64);
        if (data.image.status === 'processed') {
          const imageBytesB64 = data.image?.imageBytesB64;
          setOriginalImage(
            imageBytesB64 ? `data:image/png;base64,${imageBytesB64}` : null,
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    }
  };

  const onImageCropperOpenHandler = async () => {
    if (!originalImg && originalImageKey) {
      await getOriginalImage(originalImageKey);
    }
    setImageCropperOpen(true);
  };

  const onDropFile = (acceptedPhoto, filename) => {
    setImageCropperOpen(true);
    setImageName(filename);
    setUploadingImage(acceptedPhoto);
    updateOriginalImageKey(null);
  };

  const processImage = async (binary, hashcode, origImg = false, origKey, crop) => {
    // Validation of the image info
    const infoResponse = await getImageInfo({
      path: imageInfoPath,
      header: hashcode,
      filename: imageName,
      imageIsOriginal: origImg,
      originalKey: origKey,
      cropPosition: crop,
    });
    const { status, data } = infoResponse;

    if (status === 200) {
      // Image Uploading to S3 bucket
      const url = data.form.action;
      const { fields } = data.form;
      const uploadResponse = await uploadImage(url, fields, binary, crop);

      if (uploadResponse.status === 204) {

        // Send the image upload status value
        const response = await imageProcess({
          path: imageProcessPath,
          key: uploadResponse.key,
          method: 'post',
        });

        if (response.status === 200) {
          return uploadResponse.key;
        }
      }
    }
  };

  const onSaveImage = async (
    fullImg,
    croppedImg,
    binary,
    hashcode,
    origImgBinary,
    origImgHashcode,
    origImgHasChanged = false,
    crop,
  ) => {
    setOriginalImage(fullImg);
    setUploadingImage(null);
    updateImageUrl(croppedImg);
    setImageLoadStatus('loading');

    if (setImageUpdating) {
      setImageUpdating(true);
    }

    let origImgKey;
    if (originalImageKey && !origImgHasChanged) {
      origImgKey = originalImageKey;
    } else {
      origImgKey = await processImage(origImgBinary, origImgHashcode, true, null, crop);
      updateOriginalImageKey(origImgKey);
    }
    const imgKey = await processImage(binary, hashcode, false, origImgKey, crop);

    if (imgKey) {
      timer = setInterval(
        () => getImageStatus(imgKey),
        1000,
      );
    } else {
      setImageLoadStatus('failed');
    }
  };

  const onRemoveImage = () => {
    updateImageUrl(null);
    setOriginalImage(null);
    updateImageKey(null);
    updateOriginalImageKey(null);
    setImgInvalid(true);
    setImageLoadStatus('');
  };

  useEffect(() => {
    if (cropperOnly && openCropper) {
      onImageCropperOpenHandler();
    };
  }, [cropperOnly, openCropper]);

  useEffect(() => {
    if (errorCallback && isImgInvalid && originalImg) {
      errorCallback(errorTypes);
    }
  }, [isImgInvalid]);

  const imageUploadErrorMessage = () => {
    const { acceptType, maxFileSize } = errorTypes || {};
    if (acceptType) {
      return 'Please upload your image in one of the following formats: JPEG or PNG.';
    }
    if (maxFileSize) {
      return `Sorry, the image exceeds the maximum allowed size (${imageSizeLimit} MB).`;
    }
    return 'Something went wrong with uploading your image. Please try again.';
  };

  return !cropperOnly ? (
    <div className="image-upload-field form-item">
      {label && (<label className="form-item__label">{label}</label>)}
      <div className={clsx(`image-upload-field__uploader--${imageUploadType}`, !isImgInvalid && imgLoadStatus)}>
        <PhotoUploader
          src={imageUrl}
          imageUploadType={imageUploadType}
          handleOnDrop={onDropFile}
          handleDelete={onRemoveImage}
          setImageError={(value) => setImgInvalid(value)}
          setErrorTypes={(value) => setErrorTypes(value)}
          showImageTypeRestrictionText={false}
          imageUploadText={imageUploadText}
          isLoading={!isImgInvalid && imgLoadStatus === 'loading'}
          sizeLimit={imageSizeLimit}
        />
      </div>
      {imageUrl && (originalImg || originalImageKey) && (
        <button
          className="adjust-button semibold button--unstyled"
          onClick={() => {
            onImageCropperOpenHandler();
          }}
        >
          Adjust Image
        </button>
      )}
      {!isImgInvalid && imgLoadStatus === 'loading' && (
        <div className="image-upload-field__error text-danger">
          Updating Image...
        </div>
      )}
      {!isImgInvalid && imgLoadStatus === 'success' && (
        <div className="image-upload-field__error text-danger">
          Updated Image Successfully
        </div>
      )}
      {!isImgInvalid && imgLoadStatus === 'failed' && (
        <div className="image-upload-field__error text-danger">
          Updating Image Failed
        </div>
      )}
      {isImgInvalid && (
        <div className="image-upload-field__error text-danger">
          {imageUploadErrorMessage()}
        </div>
      )}
      <PhotoCropper
        src={uploadingImg || originalImg}
        imageUploadType={imageUploadType}
        open={openImageCropper}
        onClose={() => {
          setImageCropperOpen(false);
        }}
        onSave={onSaveImage}
        imageUploadText={imageCropperUploadText}
        sizeLimit={imageSizeLimit}
      />
    </div>
  ) : (
    <PhotoCropper
      src={uploadingImg || originalImg}
      imageUploadType={imageUploadType}
      open={openImageCropper}
      onClose={() => {
        setImageCropperOpen(false);
        setImageModalOpen(false);
      }}
      onSave={onSaveImage}
      handleDelete={onRemoveImage}
      title="Edit your image."
      showImageTypeRestrictionText={false}
      isImageLoading={originalImageKey && !originalImg}
      imageUploadText={imageCropperUploadText}
      cropperConfig={cropperConfig}
      sizeLimit={imageSizeLimit}
    />
  );
};

ImageUploadField.propTypes = {
  label: PropTypes.string,
  imageUploadType: PropTypes.string,
  imageUrl: PropTypes.string,
  originalImageKey: PropTypes.string,
  updateImageKey: PropTypes.func,
  updateOriginalImageKey: PropTypes.func,
  updateImageUrl: PropTypes.func,
  imageInfoPath: PropTypes.string,
  imageProcessPath: PropTypes.string,
  setImageUpdating: PropTypes.func,
  setImageModalOpen: PropTypes.func,
  cropperOnly: PropTypes.bool,
  openCropper: PropTypes.bool,
  imageUploadText: PropTypes.string,
  imageCropperUploadText: PropTypes.string,
};

export default ImageUploadField;
